import { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRoute, faUsers, faSatelliteDish } from '@fortawesome/free-solid-svg-icons'
import { departureMs } from './tripUtils'

const useCountUp = value => {
  const [display, setDisplay] = useState(value)
  const fromRef = useRef(value)
  useEffect(() => {
    const from = fromRef.current
    const to = value
    if (from === to) return
    const start = performance.now()
    let raf
    const step = ts => {
      const k = Math.min(1, (ts - start) / 700)
      const eased = 1 - Math.pow(1 - k, 3)
      setDisplay(Math.round(from + (to - from) * eased))
      if (k < 1) raf = requestAnimationFrame(step)
      else fromRef.current = to
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [value])
  return display
}

const Stat = ({ icon, label, value, accent, live }) => {
  const display = useCountUp(value)
  return (
    <div
      className='flex items-center gap-3.5 px-4 py-3 rounded-2xl border'
      style={{ background: 'var(--panel)', borderColor: 'var(--border)', boxShadow: 'var(--shadow-card)' }}
    >
      <div className='flex items-center justify-center w-11 h-11 rounded-xl shrink-0' style={{ background: accent.soft, color: accent.color }}>
        <FontAwesomeIcon icon={icon} className='text-base' />
      </div>
      <div className='leading-none min-w-0'>
        <div className='flex items-center gap-1.5'>
          <span className='text-[1.7rem] font-black tabular-nums tracking-tight' style={{ color: 'var(--text)' }}>{display}</span>
          {live && display > 0 && <span className='live-blink w-2 h-2 rounded-full' style={{ background: accent.color }} />}
        </div>
        <div className='text-[11px] uppercase tracking-wider mt-1 font-medium truncate' style={{ color: 'var(--text-soft)' }}>{label}</div>
      </div>
    </div>
  )
}

const HourlyBars = ({ todayTrips }) => {
  const buckets = new Array(24).fill(0)
  todayTrips.forEach(t => {
    const ms = departureMs(t)
    if (!Number.isNaN(ms)) buckets[new Date(ms).getHours()] += 1
  })
  const max = Math.max(1, ...buckets)
  const currentHour = new Date().getHours()
  return (
    <div className='hidden md:flex flex-col justify-center px-4 py-3 rounded-2xl border' style={{ background: 'var(--panel)', borderColor: 'var(--border)', boxShadow: 'var(--shadow-card)' }}>
      <div className='flex items-center justify-between mb-2'>
        <div className='text-[11px] uppercase tracking-wider font-medium' style={{ color: 'var(--text-soft)' }}>Salidas por hora</div>
        <div className='text-[10px] tabular-nums' style={{ color: 'var(--text-faint)' }}>{currentHour}:00</div>
      </div>
      <div className='flex items-end gap-[3px] h-10'>
        {buckets.map((count, h) => {
          const isNow = h === currentHour
          return (
            <div
              key={h}
              title={`${h}:00 · ${count} viaje${count === 1 ? '' : 's'}`}
              className='flex-1 rounded-t-[3px] transition-all duration-500'
              style={{
                height: `${(count / max) * 100}%`,
                minHeight: count > 0 ? 4 : 2,
                background: isNow ? 'var(--coral)' : count > 0 ? 'var(--amber)' : 'var(--border-strong)'
              }}
            />
          )
        })}
      </div>
    </div>
  )
}

const StatsBar = ({ todayTrips, liveCount, passengersToday }) => {
  return (
    <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
      <Stat icon={faRoute} label='Viajes hoy' value={todayTrips.length} accent={{ color: 'var(--amber)', soft: 'var(--amber-soft)' }} />
      <Stat icon={faUsers} label='Pasajeros hoy' value={passengersToday} accent={{ color: 'var(--green)', soft: 'var(--green-soft)' }} />
      <Stat icon={faSatelliteDish} label='En viaje ahora' value={liveCount} accent={{ color: 'var(--coral)', soft: 'var(--coral-soft)' }} live />
      <HourlyBars todayTrips={todayTrips} />
    </div>
  )
}

export default StatsBar
